// src/Views/AdminCrypto/AdminCrypto.js
// Bot Users Management — Activate, Deactivate, Monitor subscribers
// UPDATED: Feb 19, 2026 — Added Ignis enrollment toggle (creates config/bot doc if missing)

import React, { useState, useEffect } from 'react';
import './AdminCrypto.css';
import { useAuth } from '../../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Icon } from '@iconify/react';

const functions = getFunctions();

const AdminCrypto = () => {
    const { user, profile, loading } = useAuth();
    const isAdmin = !!profile?.isAdmin;

    // ============================================
    // BOT USERS STATE
    // ============================================
    const [botUsers, setBotUsers] = useState([]);
    const [botLoading, setBotLoading] = useState(false);
    const [botMessage, setBotMessage] = useState('');
    const [activatingUid, setActivatingUid] = useState(null);
    const [activateForm, setActivateForm] = useState({
        txHash: '',
        paymentAmount: '',
        durationDays: '30',
    });

    // ============================================
    // IGNIS ENROLLMENT TOGGLE STATE
    // ============================================
    const [enrollmentEnabled, setEnrollmentEnabled] = useState(true);
    const [enrollmentLoading, setEnrollmentLoading] = useState(false);
    const [enrollmentMsg, setEnrollmentMsg] = useState('');
    const [maintenanceMsg, setMaintenanceMsg] = useState('');
    const [slotsTaken, setSlotsTaken] = useState(0);
    const [slotsTotal, setSlotsTotal] = useState(15);

    // ============================================
    // FETCH BOT INFO (enrollment status + slots)
    // ============================================
    const fetchBotInfo = async () => {
        try {
            const getBotInfoFn = httpsCallable(functions, 'getBotInfo');
            const result = await getBotInfoFn();
            const data = result.data;
            setEnrollmentEnabled(data.enrollmentEnabled !== false);
            setMaintenanceMsg(data.maintenanceMessage || '');
            setSlotsTaken(data.slotsTaken || 0);
            setSlotsTotal(data.slotsTotal || 15);
        } catch (err) {
            console.error('fetchBotInfo error:', err);
            // If the function doesn't exist yet, default to enabled
            setEnrollmentEnabled(true);
        }
    };

    // ============================================
    // TOGGLE ENROLLMENT
    // ============================================
    const handleToggleEnrollment = async () => {
        const newState = !enrollmentEnabled;
        const action = newState ? 'ENABLE' : 'DISABLE';

        if (!window.confirm(
            newState
                ? '✅ Enable Ignis enrollment?\n\nUsers will see the enrollment form on the Crypto AI page.'
                : '🔒 Disable Ignis enrollment?\n\nUsers will see a "Coming Soon" lock screen.\nUseful for App Store submissions.'
        )) return;

        setEnrollmentLoading(true);
        setEnrollmentMsg('');

        try {
            const toggleFn = httpsCallable(functions, 'adminToggleBotEnrollment');
            const result = await toggleFn({
                enabled: newState,
                message: newState ? '' : (maintenanceMsg || 'Launching soon — check back shortly.'),
            });

            if (result.data.success) {
                setEnrollmentEnabled(newState);
                setEnrollmentMsg(`✅ Enrollment ${newState ? 'ENABLED' : 'DISABLED'}`);
            } else {
                setEnrollmentMsg('❌ Toggle failed');
            }
        } catch (err) {
            console.error('Toggle error:', err);
            setEnrollmentMsg(`❌ ${err.message}`);
        } finally {
            setEnrollmentLoading(false);
        }
    };

    // ============================================
    // FETCH BOT USERS
    // ============================================
    const fetchBotUsers = async () => {
        setBotLoading(true);
        setBotMessage('');
        try {
            const listUsers = httpsCallable(functions, 'adminListBotUsers');
            const result = await listUsers();
            if (result.data.success) {
                setBotUsers(result.data.users || []);
                setBotMessage(`Loaded ${result.data.users?.length || 0} bot users`);
            } else {
                setBotMessage('Failed to load bot users');
            }
        } catch (err) {
            console.error('fetchBotUsers error:', err);
            setBotMessage(`Error: ${err.message}`);
        } finally {
            setBotLoading(false);
        }
    };

    // ============================================
    // ACTIVATE USER
    // ============================================
    const handleActivateUser = async (uid) => {
        if (!activateForm.txHash && !window.confirm('No TX hash entered. Activate anyway?')) return;

        const tierUser = botUsers.find(u => u.uid === uid);
        const tierPrices = { bronze: 500, silver: 1000, gold: 2000 };
        const expectedAmount = tierPrices[tierUser?.tier] || 500;

        setBotMessage('Activating...');
        try {
            const activate = httpsCallable(functions, 'adminActivateBotUser');
            const result = await activate({
                targetUid: uid,
                txHash: activateForm.txHash || 'manual',
                paymentAmount: Number(activateForm.paymentAmount) || expectedAmount,
                paymentCurrency: 'USDT',
                durationDays: Number(activateForm.durationDays) || 30,
            });

            if (result.data.success) {
                setBotMessage(`✅ ${result.data.message}`);
                setActivatingUid(null);
                setActivateForm({ txHash: '', paymentAmount: '', durationDays: '30' });
                fetchBotUsers();
            } else {
                setBotMessage('❌ Activation failed');
            }
        } catch (err) {
            console.error('Activate error:', err);
            setBotMessage(`❌ ${err.message}`);
        }
    };

    // ============================================
    // DEACTIVATE USER
    // ============================================
    const handleDeactivateUser = async (uid, displayName) => {
        const reason = window.prompt(`Deactivate ${displayName || uid}?\n\nEnter reason (optional):`);
        if (reason === null) return;

        setBotMessage('Deactivating...');
        try {
            const deactivate = httpsCallable(functions, 'adminDeactivateBotUser');
            const result = await deactivate({
                targetUid: uid,
                reason: reason || 'Admin deactivation',
            });

            if (result.data.success) {
                setBotMessage(`✅ User deactivated`);
                fetchBotUsers();
            }
        } catch (err) {
            console.error('Deactivate error:', err);
            setBotMessage(`❌ ${err.message}`);
        }
    };

    // Load on mount
    useEffect(() => {
        if (isAdmin) {
            fetchBotUsers();
            fetchBotInfo();
        }
    }, [isAdmin]);

    const formatDate = (ts) => {
        if (!ts) return 'N/A';
        try {
            const d = ts.toDate ? ts.toDate() : new Date(ts._seconds ? ts._seconds * 1000 : ts);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return 'N/A';
        }
    };

    const isExpired = (ts) => {
        if (!ts) return false;
        try {
            const d = ts.toDate ? ts.toDate() : new Date(ts._seconds ? ts._seconds * 1000 : ts);
            return d < new Date();
        } catch {
            return false;
        }
    };

    // ============================================
    // RENDER
    // ============================================
    if (loading) return <div className="adminc__loading">Loading...</div>;
    if (!isAdmin) return <div className="adminc__notice">Access denied.</div>;

    return (
        <section className="adminc">
            <div className="adminc__inner">
                <header className="adminc__header">
                    <p className="adminc__eyebrow">Internal tool</p>
                    <h1 className="adminc__title">Ignis Admin</h1>
                </header>

                {/* ============================================ */}
                {/* IGNIS ENROLLMENT TOGGLE                      */}
                {/* ============================================ */}
                <div style={{
                    background: enrollmentEnabled 
                        ? 'rgba(74, 222, 128, 0.04)' 
                        : 'rgba(248, 113, 113, 0.04)',
                    border: `1px solid ${enrollmentEnabled 
                        ? 'rgba(74, 222, 128, 0.2)' 
                        : 'rgba(248, 113, 113, 0.2)'}`,
                    borderRadius: '14px',
                    padding: '20px',
                    marginBottom: '24px',
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: enrollmentMsg ? '12px' : '0',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Icon 
                                icon={enrollmentEnabled ? "solar:shield-check-bold" : "solar:lock-bold"} 
                                style={{ 
                                    fontSize: '1.5rem', 
                                    color: enrollmentEnabled ? '#4ade80' : '#f87171' 
                                }} 
                            />
                            <div>
                                <p style={{ 
                                    fontWeight: 700, 
                                    color: '#fff', 
                                    fontSize: '0.95rem', 
                                    marginBottom: '2px' 
                                }}>
                                    Ignis Enrollment
                                </p>
                                <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                    {enrollmentEnabled 
                                        ? `Live — ${slotsTaken}/${slotsTotal} slots taken` 
                                        : 'Paused — users see "Coming Soon" lock screen'}
                                </p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* Maintenance message input (shown when disabled) */}
                            {!enrollmentEnabled && (
                                <input
                                    type="text"
                                    value={maintenanceMsg}
                                    onChange={(e) => setMaintenanceMsg(e.target.value)}
                                    placeholder="Lock screen message (optional)"
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: '1px solid #334155',
                                        background: '#0f172a',
                                        color: '#e2e8f0',
                                        fontSize: '0.8rem',
                                        width: '250px',
                                    }}
                                />
                            )}

                            {/* Toggle button */}
                            <button
                                onClick={handleToggleEnrollment}
                                disabled={enrollmentLoading}
                                style={{
                                    padding: '10px 20px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    cursor: enrollmentLoading ? 'wait' : 'pointer',
                                    background: enrollmentEnabled 
                                        ? 'rgba(248, 113, 113, 0.15)' 
                                        : 'rgba(74, 222, 128, 0.15)',
                                    color: enrollmentEnabled ? '#f87171' : '#4ade80',
                                    border: `1px solid ${enrollmentEnabled 
                                        ? 'rgba(248, 113, 113, 0.3)' 
                                        : 'rgba(74, 222, 128, 0.3)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    opacity: enrollmentLoading ? 0.6 : 1,
                                    minWidth: '140px',
                                    justifyContent: 'center',
                                }}
                            >
                                <Icon icon={enrollmentEnabled 
                                    ? "solar:lock-bold" 
                                    : "solar:shield-check-bold"} 
                                />
                                {enrollmentLoading 
                                    ? 'Updating...' 
                                    : enrollmentEnabled 
                                        ? 'Disable Enrollment' 
                                        : 'Enable Enrollment'}
                            </button>
                        </div>
                    </div>

                    {enrollmentMsg && (
                        <p style={{
                            fontSize: '0.8rem',
                            color: enrollmentMsg.startsWith('✅') ? '#4ade80' : '#f87171',
                            marginTop: '8px',
                        }}>
                            {enrollmentMsg}
                        </p>
                    )}
                </div>

                {/* ============================================ */}
                {/* HEADER + REFRESH                             */}
                {/* ============================================ */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                    <h2 className="adminc__section-title" style={{ margin: 0 }}>
                        Subscribers ({botUsers.length})
                    </h2>
                    <button
                        onClick={fetchBotUsers}
                        disabled={botLoading}
                        style={{
                            background: '#1e293b',
                            border: '1px solid #334155',
                            color: '#94a3b8',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        <Icon icon="solar:refresh-linear" />
                        {botLoading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>

                {botMessage && (
                    <p style={{
                        fontSize: '0.85rem',
                        color: botMessage.startsWith('✅') ? '#4ade80' : botMessage.startsWith('❌') ? '#f87171' : '#94a3b8',
                        marginBottom: '16px',
                    }}>
                        {botMessage}
                    </p>
                )}

                {/* SUMMARY CARDS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
                    <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                        <p style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '4px' }}>Total</p>
                        <p style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 800, fontFamily: 'Kanit, sans-serif' }}>{botUsers.length}</p>
                    </div>
                    <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                        <p style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '4px' }}>Active</p>
                        <p style={{ color: '#4ade80', fontSize: '1.5rem', fontWeight: 800, fontFamily: 'Kanit, sans-serif' }}>{botUsers.filter(u => u.active).length}</p>
                    </div>
                    <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                        <p style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '4px' }}>Pending</p>
                        <p style={{ color: '#facc15', fontSize: '1.5rem', fontWeight: 800, fontFamily: 'Kanit, sans-serif' }}>{botUsers.filter(u => u.pendingActivation).length}</p>
                    </div>
                    <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                        <p style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '4px' }}>Revenue</p>
                        <p style={{ color: '#c084fc', fontSize: '1.5rem', fontWeight: 800, fontFamily: 'Kanit, sans-serif' }}>
                            ${botUsers.filter(u => u.active).reduce((sum, u) => {
                                const prices = { bronze: 500, silver: 1000, gold: 2000 };
                                return sum + (prices[u.tier] || 0);
                            }, 0).toLocaleString()}
                        </p>
                    </div>
                </div>

                {/* PENDING USERS */}
                {botUsers.filter(u => u.pendingActivation && !u.active).length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ color: '#facc15', fontSize: '0.9rem', fontWeight: 700, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Icon icon="solar:clock-circle-bold" /> Pending Activation
                        </h3>
                        <div style={{ display: 'grid', gap: '10px' }}>
                            {botUsers.filter(u => u.pendingActivation && !u.active).map(u => (
                                <div key={u.uid} style={{
                                    background: 'rgba(250, 204, 21, 0.04)',
                                    border: '1px solid rgba(250, 204, 21, 0.2)',
                                    borderRadius: '12px',
                                    padding: '16px',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                        <div>
                                            <p style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', marginBottom: '2px' }}>
                                                {u.displayName || 'Unknown User'}
                                            </p>
                                            <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{u.email}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '3px 12px',
                                                borderRadius: '999px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                background: u.tier === 'gold' ? '#facc15' : u.tier === 'silver' ? '#94a3b8' : '#cd7f32',
                                                color: '#000',
                                            }}>
                                                {u.tier || 'bronze'}
                                            </span>
                                            <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '4px' }}>
                                                Cap: ${u.capitalCap?.toLocaleString() || '2,000'}
                                            </p>
                                        </div>
                                    </div>

                                    {activatingUid === u.uid ? (
                                        <div style={{ background: '#0f172a', borderRadius: '10px', padding: '14px', marginTop: '10px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                                <div>
                                                    <label style={{ display: 'block', color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '4px' }}>TX Hash</label>
                                                    <input
                                                        type="text"
                                                        value={activateForm.txHash}
                                                        onChange={(e) => setActivateForm(p => ({ ...p, txHash: e.target.value }))}
                                                        placeholder="Paste USDT tx hash"
                                                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0', fontSize: '0.8rem', boxSizing: 'border-box' }}
                                                    />
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '4px' }}>Amount ($)</label>
                                                    <input
                                                        type="number"
                                                        value={activateForm.paymentAmount}
                                                        onChange={(e) => setActivateForm(p => ({ ...p, paymentAmount: e.target.value }))}
                                                        placeholder={`${u.tier === 'gold' ? '2000' : u.tier === 'silver' ? '1000' : '500'}`}
                                                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0', fontSize: '0.8rem', boxSizing: 'border-box' }}
                                                    />
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '4px' }}>Days</label>
                                                    <input
                                                        type="number"
                                                        value={activateForm.durationDays}
                                                        onChange={(e) => setActivateForm(p => ({ ...p, durationDays: e.target.value }))}
                                                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0', fontSize: '0.8rem', boxSizing: 'border-box' }}
                                                    />
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => handleActivateUser(u.uid)}
                                                    style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#4ade80', color: '#000', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}
                                                >
                                                    ✅ Confirm Activation
                                                </button>
                                                <button
                                                    onClick={() => { setActivatingUid(null); setActivateForm({ txHash: '', paymentAmount: '', durationDays: '30' }); }}
                                                    style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #334155', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '0.85rem' }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setActivatingUid(u.uid)}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(74, 222, 128, 0.3)',
                                                background: 'rgba(74, 222, 128, 0.08)',
                                                color: '#4ade80',
                                                cursor: 'pointer',
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                marginTop: '8px',
                                            }}
                                        >
                                            Activate This User
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ACTIVE USERS */}
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: '#4ade80', fontSize: '0.9rem', fontWeight: 700, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon icon="solar:check-circle-bold" /> Active Subscribers
                    </h3>
                    {botUsers.filter(u => u.active).length === 0 ? (
                        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>No active subscribers yet.</p>
                    ) : (
                        <div style={{ display: 'grid', gap: '10px' }}>
                            {botUsers.filter(u => u.active).map(u => (
                                <div key={u.uid} style={{
                                    background: '#1e293b',
                                    border: '1px solid #334155',
                                    borderRadius: '12px',
                                    padding: '14px 16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                    <div>
                                        <p style={{ fontWeight: 600, color: '#fff', fontSize: '0.9rem', marginBottom: '2px' }}>
                                            {u.displayName || 'Unknown'}
                                        </p>
                                        <p style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                            {u.email} · Expires: {formatDate(u.expiresAt)}
                                            {isExpired(u.expiresAt) && <span style={{ color: '#f87171', marginLeft: '6px' }}>(EXPIRED)</span>}
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{
                                            padding: '3px 12px',
                                            borderRadius: '999px',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            background: u.tier === 'gold' ? '#facc15' : u.tier === 'silver' ? '#94a3b8' : '#cd7f32',
                                            color: '#000',
                                        }}>
                                            {u.tier}
                                        </span>
                                        <span style={{ color: '#64748b', fontSize: '0.8rem' }}>${u.capitalCap?.toLocaleString()}</span>
                                        <button
                                            onClick={() => handleDeactivateUser(u.uid, u.displayName)}
                                            style={{
                                                background: 'rgba(248, 113, 113, 0.1)',
                                                border: '1px solid rgba(248, 113, 113, 0.3)',
                                                color: '#f87171',
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Deactivate
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* INACTIVE / EXPIRED */}
                {botUsers.filter(u => !u.active && !u.pendingActivation).length > 0 && (
                    <div>
                        <h3 style={{ color: '#64748b', fontSize: '0.9rem', fontWeight: 700, marginBottom: '12px' }}>
                            Inactive / Expired
                        </h3>
                        <div style={{ display: 'grid', gap: '8px' }}>
                            {botUsers.filter(u => !u.active && !u.pendingActivation).map(u => (
                                <div key={u.uid} style={{
                                    background: '#0f172a',
                                    border: '1px solid #1e293b',
                                    borderRadius: '10px',
                                    padding: '12px 16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    opacity: 0.7,
                                }}>
                                    <div>
                                        <p style={{ fontWeight: 500, color: '#94a3b8', fontSize: '0.85rem' }}>
                                            {u.displayName || 'Unknown'} · {u.email}
                                        </p>
                                    </div>
                                    <span style={{ color: '#475569', fontSize: '0.75rem' }}>{u.tier} · ${u.capitalCap?.toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default AdminCrypto;